<!-- app\javascript\dashboard\routes\dashboard\bulkmessage\components\ContactsPagination.vue -->
<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
    default: 1,
  },
  totalPages: {
    type: Number,
    required: true,
    default: 1,
  },
  pageSize: {
    type: Number,
    required: true,
    default: 25,
  },
  pageSizeOptions: {
    type: Array,
    default: () => [10, 25, 50, 100, 200],
  },
  showPageSizeSelector: {
    type: Boolean,
    default: true,
  },
  currentItemsCount: {
    type: Number,
    default: 0,
  },
  totalItemsCount: {
    type: Number,
    default: 0,
  },
  itemName: {
    type: String,
    default: 'item',
  },
});

const emit = defineEmits(['page-change', 'page-size-change']);

// Responsive handling
const isMobile = ref(false);
const checkMobile = () => {
  isMobile.value = window.innerWidth < 640;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

// Make sure totalPages is at least 1
const safeTotalPages = computed(() => Math.max(props.totalPages || 1, 1));

// Computed properties
const canPreviousPage = computed(() => props.currentPage > 1);
const canNextPage = computed(() => props.currentPage < safeTotalPages.value);

// Calculate visible page numbers with ellipsis for many pages
const visiblePageNumbers = computed(() => {
  const totalPages = safeTotalPages.value;
  const currentPage = props.currentPage;

  if (totalPages <= 7) {
    // If we have 7 or fewer pages, show all pages
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Always include first and last page
  const result = [1];

  // Calculate middle pages to show
  if (currentPage <= 3) {
    // Near the start, show 1, 2, 3, 4, 5, ..., totalPages
    result.push(2, 3, 4, 5, '...', totalPages);
  } else if (currentPage >= totalPages - 2) {
    // Near the end, show 1, ..., totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages
    result.push(
      '...',
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages
    );
  } else {
    // In the middle, show 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
    result.push(
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages
    );
  }

  // Sort and deduplicate
  return [...new Set(result)].sort((a, b) => {
    if (a === '...') return 0;
    if (b === '...') return 0;
    return a - b;
  });
});

// Methods
const handlePageChange = page => {
  // Converter para número e validar
  const newPage = parseInt(page, 10);

  if (isNaN(newPage) || newPage < 1) {
    console.warn(`Tentativa de mudar para página inválida: ${page}`);
    return;
  }

  // Garantir que não ultrapasse o total de páginas
  const validPage = Math.min(newPage, safeTotalPages.value);

  if (validPage !== props.currentPage) {
    emit('page-change', validPage);
  }
};

const handleFirstPage = () => {
  if (canPreviousPage.value) {
    emit('page-change', 1);
  }
};

const handlePreviousPage = () => {
  if (canPreviousPage.value) {
    emit('page-change', props.currentPage - 1);
  }
};

const handleNextPage = () => {
  if (canNextPage.value) {
    emit('page-change', props.currentPage + 1);
  }
};

const handleLastPage = () => {
  if (canNextPage.value) {
    emit('page-change', safeTotalPages.value);
  }
};

const handlePageSizeChange = event => {
  const newSize = parseInt(event.target.value, 10);

  if (isNaN(newSize) || newSize <= 0) {
    console.warn(`Tamanho de página inválido: ${event.target.value}`);
    return;
  }

  // Verificar se está entre os tamanhos permitidos
  if (props.pageSizeOptions.includes(newSize)) {
    emit('page-size-change', newSize);
  } else {
    console.warn(
      `Tamanho de página ${newSize} não está entre os valores permitidos`
    );
  }
};

watch(
  () => props.totalPages,
  (newValue) => {

    // Se o valor for inválido, usar um valor seguro
    const safeNewValue = Math.max(1, newValue || 1);

    // Se a página atual estiver além do novo total, ir para a última página
    if (props.currentPage > safeNewValue && safeNewValue > 0) {
      emit('page-change', safeNewValue);
    }
  },
  { immediate: true }
);

watch(
  () => props.pageSize,
  (newValue, oldValue) => {
    console.log(`pageSize changed from ${oldValue} to ${newValue}`);
  }
);
</script>

<template>
  <div class="pagination-container">
    <!-- Items count info -->
    <span
      v-if="totalItemsCount > 0"
      class="text-sm text-n-slate-11 flex-shrink-0"
    >
      Mostrando {{ currentItemsCount }} de {{ totalItemsCount }} {{ itemName
      }}{{ totalItemsCount !== 1 ? 's' : '' }}
    </span>

    <!-- Navigation buttons wrapper -->
    <div class="flex items-center gap-1">
      <!-- First page button -->
      <button
        class="pagination-button"
        :disabled="!canPreviousPage"
        aria-label="First page"
        @click="handleFirstPage"
      >
        <i class="i-lucide-chevrons-left" />
      </button>

      <!-- Previous page button -->
      <button
        class="pagination-button"
        :disabled="!canPreviousPage"
        aria-label="Previous page"
        @click="handlePreviousPage"
      >
        <i class="i-lucide-chevron-left" />
      </button>

      <!-- Pages -->
      <div class="flex items-center mx-2">
        <!-- Page info - shown on small screens when page numbers are hidden -->
        <span
          class="text-xs text-n-slate-11 dark:text-n-slate-9 mr-2 sm:hidden"
        >
          {{ currentPage }}/{{ safeTotalPages }}
        </span>
        <template v-for="pageNumber in visiblePageNumbers" :key="pageNumber">
          <button
            v-if="pageNumber !== '...'"
            class="pagination-page-button"
            :class="{ active: pageNumber === currentPage }"
            @click="handlePageChange(pageNumber)"
          >
            {{ pageNumber }}
          </button>
          <span v-else class="pagination-ellipsis">...</span>
        </template>
      </div>

      <!-- Next page button -->
      <button
        class="pagination-button"
        :disabled="!canNextPage"
        aria-label="Next page"
        @click="handleNextPage"
      >
        <i class="i-lucide-chevron-right" />
      </button>

      <!-- Last page button -->
      <button
        class="pagination-button"
        :disabled="!canNextPage"
        aria-label="Last page"
        @click="handleLastPage"
      >
        <i class="i-lucide-chevrons-right" />
      </button>
    </div>

    <!-- Page size selector - responsive version -->
    <div v-if="showPageSizeSelector" class="flex items-center flex-shrink-0">
      <span
        class="text-sm text-n-slate-11 mr-2 inline"
        >Exibir:</span
      >
      <select
        class="text-sm border border-n-slate-3 dark:border-n-slate-7 rounded px-2 py-1 bg-n-background dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-11 mb-0"
        :value="pageSize"
        @change="handlePageSizeChange($event)"
      >
        <option v-for="option in pageSizeOptions" :key="option" :value="option">
          {{ option }}{{ !isMobile ? ' por página' : '' }}
        </option>
      </select>
    </div>
  </div>
</template>

<style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.pagination-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 2px;
  border-radius: 4px;
  background-color: var(--n-background, white);
  border: 1px solid var(--n-slate-3, #e2e8f0);
  color: var(--n-slate-9, #64748b);
  transition: all 0.2s;
}

.dark .pagination-button {
  background-color: var(--n-slate-3, #1e293b);
  border-color: var(--n-slate-7, #334155);
  color: var(--n-slate-9, #94a3b8);
}

.pagination-button:hover:not(:disabled) {
  background-color: var(--n-slate-2, #f1f5f9);
  color: var(--n-slate-11, #475569);
  border-color: var(--n-slate-4, #cbd5e1);
}

.dark .pagination-button:hover:not(:disabled) {
  background-color: var(--n-slate-4, #334155);
  color: var(--n-slate-11, #e2e8f0);
  border-color: var(--n-slate-6, #475569);
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-page-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  margin: 0 2px;
  border-radius: 4px;
  background-color: var(--n-background, white);
  border: 1px solid var(--n-slate-3, #e2e8f0);
  color: var(--n-slate-9, #64748b);
  transition: all 0.2s;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  box-sizing: border-box;
}

.dark .pagination-page-button {
  background-color: var(--n-slate-3, #1e293b);
  border-color: var(--n-slate-7, #334155);
  color: var(--n-slate-9, #94a3b8);
}

.pagination-page-button:hover {
  background-color: var(--n-slate-2, #f1f5f9);
  color: var(--n-slate-11, #475569);
  border-color: var(--n-slate-4, #cbd5e1);
}

.dark .pagination-page-button:hover {
  background-color: var(--n-slate-4, #334155);
  color: var(--n-slate-11, #e2e8f0);
  border-color: var(--n-slate-6, #475569);
}

.pagination-page-button.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.dark .pagination-page-button.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.5);
}

.pagination-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--n-slate-11, #64748b);
}

.dark .pagination-ellipsis {
  color: var(--n-slate-9, #94a3b8);
}

@media (max-width: 640px) {
  .pagination-container {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }

  .pagination-container > span:first-child {
    width: 100%;
    text-align: center;
    margin-bottom: 0.5rem;
  }

  .pagination-button,
  .pagination-page-button {
    width: 28px;
    min-width: 28px;
    height: 28px;
    font-size: 12px;
  }
}
</style>
